/**
 * Practice clock — a local, operator-controlled transport.
 *
 * OPBridge clock-design ask, Build 1 (2026-07-17). The PWA clock is
 * DISPLAY-ONLY: it drives a playhead + LFO/timeline preview visuals, never
 * output (all execution + MIDI/OSC/DMX runs from OPBridge's gold clock). So
 * this transport doesn't need sub-ms accuracy — it needs to be controllable
 * when there's no live OPBridge anchor (disconnected, OR connected-but-
 * Ableton-stopped) so the operator can rehearse visuals with no laptop.
 *
 * This module is the pure local-transport MODEL: bpm, play/stop, time-sig,
 * a downbeat "epoch" (bumped on tap-to-set-beat-1), and tap-tempo. It holds
 * NO reference to smoothClock — App owns that instance and wires these
 * signals into it (anchor/freeze/quantum) whenever the live clock is absent
 * (see App's `isSlaved` gate). Keeping the model clock-agnostic keeps the
 * precedence/handoff logic in one place (App) and this file trivially
 * testable.
 *
 * Persistence: bpm reuses the existing `opbuttons.bpm` key (bpmStorage);
 * play-state + time-sig get their own keys so a reload resumes where the
 * operator left off.
 */

import { createSignal, type Accessor } from 'solid-js';
import { loadBpm, saveBpm, clampBpm } from './bpmStorage.ts';

const PLAYING_KEY = 'opbuttons.practice.playing';
const SIG_KEY = 'opbuttons.practice.sig';

/** Reset the tap-tempo sample window after this long between taps — a new
 *  tap after a pause starts a fresh tempo estimate, not an average with a
 *  stale interval. */
const TAP_TEMPO_RESET_MS = 2_000;
/** Time signatures the header's sig control cycles through (all /4). */
export const PRACTICE_SIGS: ReadonlyArray<readonly [number, number]> = [
  [2, 4], [3, 4], [4, 4], [5, 4], [6, 4], [7, 4],
];

// ─── BPM ──────────────────────────────────────────────────────
const [bpmSig, setBpmSig] = createSignal(loadBpm());
/** Current practice tempo (BPM). Also what smoothClock advances at while the
 *  practice clock is the active source. */
export const practiceBpm: Accessor<number> = bpmSig;
/** Set + persist the practice tempo. Clamped to the shared BPM range. */
export function setPracticeBpm(n: number): void {
  const c = clampBpm(n);
  setBpmSig(c);
  saveBpm(c);
}

// ─── Play / stop ──────────────────────────────────────────────
function loadPlaying(): boolean {
  if (typeof localStorage === 'undefined') return false;
  const raw = localStorage.getItem(PLAYING_KEY);
  // Default to STOPPED (user preference 2026-07-17): a fresh PWA boots with
  // the metronome parked at the downbeat, not free-running. Persisted state
  // still wins on reload.
  return raw === null ? false : raw === '1';
}
function savePlaying(v: boolean): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(PLAYING_KEY, v ? '1' : '0');
}
const [playingSig, setPlayingSig] = createSignal(loadPlaying());
/** Whether the practice transport is running. */
export const practiceIsPlaying: Accessor<boolean> = playingSig;
/** Set + persist the practice play state. */
export function setPracticePlaying(v: boolean): void {
  setPlayingSig(v);
  savePlaying(v);
}
/** Toggle play/stop. */
export function togglePracticePlaying(): void {
  setPracticePlaying(!playingSig());
}

// ─── Time signature ───────────────────────────────────────────
function loadSig(): [number, number] {
  if (typeof localStorage === 'undefined') return [4, 4];
  const raw = localStorage.getItem(SIG_KEY);
  if (!raw) return [4, 4];
  const parts = raw.split('/');
  const n = Number(parts[0]);
  const d = Number(parts[1]);
  if (Number.isFinite(n) && Number.isFinite(d) && n > 0 && d > 0) return [n, d];
  return [4, 4];
}
function saveSig(n: number, d: number): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(SIG_KEY, `${n}/${d}`);
}
const initSig = loadSig();
const [sigNumSig, setSigNumSig] = createSignal(initSig[0]);
const [sigDenSig, setSigDenSig] = createSignal(initSig[1]);
/** Beats-per-bar numerator (top of the time signature). */
export const practiceSigNum: Accessor<number> = sigNumSig;
/** Time-signature denominator (bottom). */
export const practiceSigDen: Accessor<number> = sigDenSig;
/** Set + persist the time signature. */
export function setPracticeSig(n: number, d: number): void {
  setSigNumSig(n);
  setSigDenSig(d);
  saveSig(n, d);
}
/** Advance to the next preset time signature (header sig control). */
export function cyclePracticeSig(): void {
  const cur = `${sigNumSig()}/${sigDenSig()}`;
  const idx = PRACTICE_SIGS.findIndex(([n, d]) => `${n}/${d}` === cur);
  const next = PRACTICE_SIGS[(idx + 1) % PRACTICE_SIGS.length]!;
  setPracticeSig(next[0], next[1]);
}
/** Bar length in quarter-note beats, for smoothClock's phase wrap. 4/4→4,
 *  3/4→3, 6/8→3, 7/8→3.5. */
export function practiceQuantum(): number {
  return sigNumSig() * (4 / sigDenSig());
}

// ─── Downbeat (set beat 1) ────────────────────────────────────
const [downbeatEpochSig, setDownbeatEpochSig] = createSignal(0);
/** Bumped each time the operator sets "this moment = beat 1". App watches
 *  this to re-anchor smoothClock's phase to 0. */
export const practiceDownbeatEpoch: Accessor<number> = downbeatEpochSig;
/** Set the current instant as beat 1 (downbeat). Starts the transport first
 *  if it was stopped, so one tap both starts and aligns. */
export function tapPracticeDownbeat(): void {
  if (!playingSig()) setPracticePlaying(true);
  setDownbeatEpochSig(downbeatEpochSig() + 1);
}

// ─── Tap tempo ────────────────────────────────────────────────
let tapTimes: number[] = [];
/** Register a tap-tempo hit. Two or more taps within the reset window set
 *  the BPM from the average inter-tap interval. A gap longer than
 *  TAP_TEMPO_RESET_MS starts a fresh estimate. */
export function tapTempo(): void {
  const now = performance.now();
  const last = tapTimes[tapTimes.length - 1];
  if (last !== undefined && now - last > TAP_TEMPO_RESET_MS) tapTimes = [];
  tapTimes.push(now);
  if (tapTimes.length > 4) tapTimes.shift();
  if (tapTimes.length >= 2) {
    const first = tapTimes[0]!;
    const span = now - first;
    const avgInterval = span / (tapTimes.length - 1);
    if (avgInterval > 0) setPracticeBpm(60_000 / avgInterval);
  }
}
